// Аналитика склада. Сверху — потери за период (списания, отходы заготовок,
// недостача по инвентаризации) и кто списывал: группы по людям, в строке —
// отмена ошибочного списания. Ниже — движение склада в рублях и вкладки
// «Операции» (журнал всех движений) и «По позициям» (оборотная ведомость).
import { useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarRange, ChevronRight, FileDown, Loader2, Lock, Search, Undo2, UserRound, X,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import {
  cancelStockWriteOff, getStockAnalytics, listStockMovements, STOCK_RESET_REASON,
  type StockAnalytics, type StockMovement, type StockMovementSource, type StockMovementType,
} from '@/services/admin';
import { formatRub } from '@/utils/format';
import { formatQuantity, trimNumber } from '@/utils/units';

// ─── Период ──────────────────────────────────────────────────────────────

type Preset = 'today' | '7d' | '30d' | 'month';
const PRESETS: Array<{ id: Preset; label: string }> = [
  { id: 'today', label: 'Сегодня' },
  { id: '7d', label: '7 дней' },
  { id: '30d', label: '30 дней' },
  { id: 'month', label: 'Месяц' },
];

const dateKey = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function presetRange(p: Preset): { from: string; to: string } {
  const now = new Date();
  const to = dateKey(now);
  if (p === 'today') return { from: to, to };
  if (p === 'month') return { from: dateKey(new Date(now.getFullYear(), now.getMonth(), 1)), to };
  const start = new Date(now);
  start.setDate(start.getDate() - (p === '7d' ? 6 : 29));
  return { from: dateKey(start), to };
}

const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

function dayText(key: string, withYear: boolean): string {
  const [y, m, d] = key.split('-').map(Number);
  return `${d} ${MONTHS_GEN[m - 1]}${withYear ? ` ${y}` : ''}`;
}

/** «6 октября — 4 ноября»; год — только если период не целиком в текущем году. */
function periodText(from: string, to: string): string {
  const year = String(new Date().getFullYear());
  const withYear = !from.startsWith(year) || !to.startsWith(year);
  return from === to ? dayText(from, withYear) : `${dayText(from, withYear)} — ${dayText(to, withYear)}`;
}

function lossesTitle(preset: Preset | null): string {
  switch (preset) {
    case 'today': return 'Потери сегодня';
    case '7d': return 'Потери за 7 дней';
    case '30d': return 'Потери за 30 дней';
    case 'month': return `Потери за ${MONTHS[new Date().getMonth()]}`;
    default: return 'Потери за период';
  }
}

// ─── Мелочи форматирования ───────────────────────────────────────────────

function plural(n: number, forms: [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return forms[0];
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return forms[1];
  return forms[2];
}

/** Количество со знаком: «−150 г», «+2 кг». */
function signedQty(value: number, unit: string): string {
  if (value === 0) return `0 ${unit}`;
  return `${value > 0 ? '+' : '−'}${formatQuantity(Math.abs(value), unit)}`;
}

/** Остаток бывает и в минусе — минус показываем настоящий, а не дефис. */
function qtyText(value: number, unit: string): string {
  return value < 0 ? `−${formatQuantity(-value, unit)}` : formatQuantity(value, unit);
}

function signedRub(kop: number): string {
  return `${kop > 0 ? '+' : kop < 0 ? '−' : ''}${formatRub(Math.abs(kop))}`;
}

function sharePct(part: number, whole: number): string {
  const pct = (part / whole) * 100;
  return pct < 0.1 ? 'меньше 0,1%' : `${pct.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;
}

const shortDateTime = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const shortDate = (iso: string) => new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
const shortTime = (iso: string) => new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

const movementCost = (m: StockMovement) => Math.round(Math.abs(m.quantity) * m.costPerUnit);
const reasonOf = (m: StockMovement) => m.reasonLabel ?? 'Без причины';

/**
 * Что показать в «Комментарии». У продаж, возвратов и инвентаризации текст
 * сгенерирован системой и повторяет подпись источника — его прячем. Старый бэк
 * (без source) отдаёт только comment.
 */
function noteOf(m: StockMovement): string {
  if (!m.source) return m.comment ?? '';
  if (m.reason === STOCK_RESET_REASON) return m.note ?? '';
  if (m.source === 'SALE' || m.source === 'RETURN' || m.source === 'INVENTORY') return '';
  return m.note ?? '';
}

/** Причина и комментарий одной строкой; «Снято с заказа» уже начинает свой комментарий. */
function reasonAndNote(m: StockMovement): { reason: string | null; text: string } {
  const text = noteOf(m);
  const reason = m.reasonLabel ?? null;
  return { reason: reason && text.startsWith(reason) ? null : reason, text };
}

function downloadCsv(name: string, rows: string[][]) {
  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  // Safari обрывает скачивание, если отозвать ссылку сразу после click().
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ─── Вкладка ─────────────────────────────────────────────────────────────

/** Сколько списаний за период тянем для групп «Кто списывал» — больше сервер не отдаёт. */
const WRITE_OFF_LIMIT = 1000;
/** Колонки «Кто списывал» на sm+: дата · позиция · кол-во · сумма · действие; шапка группы — по той же сетке. */
const ROW_GRID = 'sm:grid sm:grid-cols-[72px_minmax(0,1fr)_80px_104px_132px] sm:gap-x-4';

export function StockAnalyticsTab() {
  const [preset, setPreset] = useState<Preset>('30d');
  const [customOpen, setCustomOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const useCustom = customOpen && Boolean(customFrom && customTo);
  const range = useCustom ? { from: customFrom, to: customTo } : presetRange(preset);

  // Фильтр по причине — по подписи: у «без причины» и «Другое» один код OTHER.
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState<StockMovement | null>(null);

  const analytics = useQuery({
    queryKey: ['admin/stock/analytics', range.from, range.to],
    queryFn: () => getStockAnalytics(range),
    placeholderData: (prev) => prev,
  });
  const writeOffs = useQuery({
    queryKey: ['admin/stock/movements', 'write-offs', range.from, range.to],
    queryFn: () =>
      listStockMovements({
        type: 'WRITE_OFF',
        dateFrom: range.from,
        dateTo: range.to,
        withCancelled: true,
        limit: WRITE_OFF_LIMIT,
      }),
    placeholderData: (prev) => prev,
  });

  const pickPreset = (p: Preset) => {
    setPreset(p);
    setCustomOpen(false);
    setReason('');
  };
  // Свой период стартует с того, что уже на экране, — цифры не прыгают.
  const openCustom = () => {
    if (customOpen) return;
    setCustomFrom(range.from);
    setCustomTo(range.to);
    setCustomOpen(true);
  };

  const busy = (analytics.isFetching && !analytics.isLoading) || (writeOffs.isFetching && !writeOffs.isLoading);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div role="group" aria-label="Быстрый период" className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          {PRESETS.map((p) => (
            <SegmentButton key={p.id} active={!customOpen && preset === p.id} onClick={() => pickPreset(p.id)}>
              {p.label}
            </SegmentButton>
          ))}
        </div>
        {/* Отдельной кнопкой, а не пятым сегментом: на телефоне пять сегментов не влезали. */}
        <button
          type="button"
          aria-pressed={customOpen}
          onClick={openCustom}
          className={`btn-outline ${customOpen ? 'border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-50' : ''}`}
        >
          <CalendarRange size={15} aria-hidden /> Свой период
        </button>
        {customOpen ? (
          <div className="flex items-center gap-2">
            <input
              type="date"
              aria-label="Начало периода"
              className="input w-auto py-1.5"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => { setCustomFrom(e.target.value); setReason(''); }}
            />
            <span className="text-slate-400" aria-hidden>—</span>
            <input
              type="date"
              aria-label="Конец периода"
              className="input w-auto py-1.5"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => { setCustomTo(e.target.value); setReason(''); }}
            />
            <button
              type="button"
              className="btn-ghost px-2"
              onClick={() => pickPreset(preset)}
              aria-label="Закрыть свой период"
              title="Вернуться к быстрому периоду"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <span className="text-sm text-slate-500">{periodText(range.from, range.to)}</span>
        )}
        {busy && <Loader2 size={16} className="animate-spin text-slate-400" aria-label="Обновляем" />}
      </div>

      {analytics.isLoading ? (
        <LossesSkeleton />
      ) : analytics.isError || !analytics.data ? (
        <LoadError text="Не удалось загрузить аналитику склада." onRetry={() => analytics.refetch()} />
      ) : (
        <LossesPanel title={lossesTitle(useCustom ? null : preset)} data={analytics.data} />
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
        <WriteOffsByPerson
          key={`${range.from}_${range.to}`}
          rows={writeOffs.data ?? []}
          loading={writeOffs.isLoading}
          error={writeOffs.isError}
          onRetry={() => writeOffs.refetch()}
          reason={reason}
          onReason={setReason}
          onCancel={setCancelling}
        />
        <ReasonsCard data={analytics.data} loading={analytics.isLoading} reason={reason} onReason={setReason} />
      </div>

      {analytics.data && <MovementSummary data={analytics.data} />}

      <DetailsCard from={range.from} to={range.to} data={analytics.data} onCancel={setCancelling} />

      {cancelling && <CancelWriteOffModal movement={cancelling} onClose={() => setCancelling(null)} />}
    </div>
  );
}

function SegmentButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md whitespace-nowrap shrink-0 transition-colors
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
          active ? 'bg-white shadow text-brand-700' : 'text-slate-600 hover:text-slate-900'
        }`}
    >
      {children}
    </button>
  );
}

function LossesSkeleton() {
  return (
    <div className="card p-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-8 motion-safe:animate-pulse" aria-hidden>
      <div className="md:w-60 grid gap-2">
        <div className="h-3 w-28 rounded bg-slate-100" />
        <div className="h-8 w-40 rounded bg-slate-100" />
        <div className="h-3 w-48 rounded bg-slate-100" />
      </div>
      <div className="flex-1 grid gap-3">
        <div className="h-2.5 rounded-full bg-slate-100" />
        <div className="h-3 w-2/3 rounded bg-slate-100" />
      </div>
    </div>
  );
}

function SkeletonLines({ n }: { n: number }) {
  return (
    <div className="grid gap-3 motion-safe:animate-pulse" aria-hidden>
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className="h-3 rounded bg-slate-100" style={{ width: `${92 - (i % 3) * 17}%` }} />
      ))}
    </div>
  );
}

function LoadError({ text, onRetry }: { text: string; onRetry: () => void }) {
  return (
    <div className="card p-6 text-center text-sm text-slate-500">
      {text}{' '}
      <button type="button" className="text-brand-700 font-medium hover:underline" onClick={onRetry}>
        Повторить
      </button>
    </div>
  );
}

// ─── Потери ──────────────────────────────────────────────────────────────

function LossesPanel({ title, data }: { title: string; data: StockAnalytics }) {
  const s = data.summary;
  const total = s.writeOff + s.waste + s.inventoryShortage;
  const salesNet = s.sales - s.returns;
  const parts = [
    { label: 'Списания', value: s.writeOff, swatch: 'bg-rose-500' },
    { label: 'Отходы заготовок', value: s.waste, swatch: 'bg-amber-400' },
    { label: 'Недостача по инвентаризации', value: s.inventoryShortage, swatch: 'bg-rose-800' },
  ];
  return (
    <section className="card p-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-8" aria-label={title}>
      <div className="md:w-60 shrink-0">
        <h2 className="text-sm text-slate-500">{title}</h2>
        <div className={`text-3xl font-semibold tabular-nums tracking-tight mt-0.5 ${total > 0 ? 'text-rose-700' : 'text-slate-900'}`}>
          {formatRub(total)}
        </div>
        {total > 0 && salesNet > 0 && (
          <div className="text-xs text-slate-500 mt-1">
            {sharePct(total, salesNet)} от себестоимости продаж ({formatRub(salesNet)})
          </div>
        )}
      </div>
      {total > 0 ? (
        <div className="flex-1 min-w-0 grid gap-3">
          <div className="flex gap-px h-2.5 rounded-full overflow-hidden bg-slate-100" aria-hidden>
            {parts.filter((p) => p.value > 0).map((p) => (
              <div key={p.label} className={p.swatch} style={{ width: `${(p.value / total) * 100}%` }} />
            ))}
          </div>
          <dl className="flex flex-wrap gap-x-6 gap-y-1.5 text-sm">
            {parts.map((p) => (
              <div key={p.label} className={`flex items-center gap-2 ${p.value > 0 ? 'text-slate-600' : 'text-slate-500'}`}>
                <span className={`w-2.5 h-2.5 rounded-sm shrink-0 ${p.value > 0 ? p.swatch : 'bg-slate-200'}`} aria-hidden />
                <dt>{p.label}</dt>
                <dd className={`tabular-nums font-medium ${p.value > 0 ? 'text-slate-900' : ''}`}>{formatRub(p.value)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : (
        <p className="flex-1 text-sm text-slate-500">За период ничего не списывали, отходов и недостачи не было.</p>
      )}
    </section>
  );
}

// ─── Кто списывал ────────────────────────────────────────────────────────
// Списания за период по людям: кассир (по имени из смены) или админ вручную.
// Сумма группы — без отменённых; отменённые остаются в строках зачёркнутыми.

type PersonGroup = {
  key: string;
  name: string | null;
  manual: boolean;
  rows: StockMovement[];
  total: number;
  active: number;
  cancelled: number;
  shifts: number[];
};

function groupByPerson(rows: StockMovement[]): PersonGroup[] {
  const groups = new Map<string, PersonGroup>();
  for (const m of rows) {
    const manual = m.source ? m.source !== 'KASSA' : m.shiftNumber == null;
    const name = m.actorName?.trim() || null;
    const key = `${manual ? 'admin' : 'kassa'}:${(name ?? '').toLowerCase()}`;
    const g = groups.get(key) ?? { key, name, manual, rows: [], total: 0, active: 0, cancelled: 0, shifts: [] };
    g.rows.push(m);
    if (m.cancelledAt) g.cancelled++;
    else {
      g.active++;
      g.total += movementCost(m);
    }
    if (m.shiftNumber != null && !g.shifts.includes(m.shiftNumber)) g.shifts.push(m.shiftNumber);
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => b.total - a.total || b.active - a.active);
}

function groupMeta(g: PersonGroup): string {
  if (g.manual) return 'вручную в админке';
  const s = [...g.shifts].sort((a, b) => b - a);
  if (s.length === 0) return 'с кассы';
  if (s.length === 1) return `смена №${s[0]}`;
  if (s.length === 2) return `смены №${s[0]} и №${s[1]}`;
  return `${s.length} ${plural(s.length, ['смена', 'смены', 'смен'])}`;
}

/** «3 списания», «1 отменено» — частями: на телефоне в строку, на десктопе столбиком. */
function groupCount(g: PersonGroup): string[] {
  const parts = [];
  if (g.active > 0 || g.cancelled === 0) {
    parts.push(`${g.active} ${plural(g.active, ['списание', 'списания', 'списаний'])}`);
  }
  if (g.cancelled > 0) parts.push(`${g.cancelled} ${plural(g.cancelled, ['отменено', 'отменены', 'отменено'])}`);
  return parts;
}

function initials(name: string | null): string {
  if (!name) return '';
  return name
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}]/gu, ''))
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

function WriteOffsByPerson({ rows, loading, error, onRetry, reason, onReason, onCancel }: {
  rows: StockMovement[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  reason: string;
  onReason: (r: string) => void;
  onCancel: (m: StockMovement) => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const filtered = useMemo(() => (reason ? rows.filter((m) => reasonOf(m) === reason) : rows), [rows, reason]);
  // Порядок групп фиксируем на период и причину: после отмены сумма группы
  // меняется, но список не прыгает под рукой и раскрытая группа остаётся на месте.
  const order = useRef<{ reason: string; keys: string[] } | null>(null);
  const groups = useMemo(() => {
    const fresh = groupByPerson(filtered);
    const prev = order.current;
    if (!prev || prev.reason !== reason) {
      order.current = { reason, keys: fresh.map((g) => g.key) };
      return fresh;
    }
    const pos = new Map(prev.keys.map((k, i) => [k, i]));
    const known = fresh.filter((g) => pos.has(g.key)).sort((a, b) => pos.get(a.key)! - pos.get(b.key)!);
    const added = fresh.filter((g) => !pos.has(g.key));
    if (added.length) order.current = { reason, keys: [...prev.keys, ...added.map((g) => g.key)] };
    return [...known, ...added];
  }, [filtered, reason]);
  const reasonOptions = useMemo(
    () => [...new Set([...rows.map(reasonOf), ...(reason ? [reason] : [])])].sort((a, b) => a.localeCompare(b, 'ru')),
    [rows, reason],
  );
  const active = filtered.filter((m) => !m.cancelledAt);
  const sum = active.reduce((s, m) => s + movementCost(m), 0);
  const max = groups.reduce((m, g) => Math.max(m, g.total), 0);

  let subtitle = '';
  if (loading) subtitle = 'Загружаем списания…';
  else if (active.length > 0) {
    subtitle = `${reason ? `«${reason}»: ` : ''}${active.length} ${plural(active.length, ['списание', 'списания', 'списаний'])} на ${formatRub(sum)}`;
    if (groups.length > 1) subtitle += ' · сначала самые крупные';
  }

  return (
    <section className="card overflow-hidden min-w-0" aria-labelledby="writeoffs-by-person">
      <div className="px-4 sm:px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 id="writeoffs-by-person" className="font-semibold">Кто списывал</h2>
          {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        </div>
        {reasonOptions.length > 1 && (
          <select
            className="input w-auto"
            value={reason}
            onChange={(e) => onReason(e.target.value)}
            aria-label="Причина списания"
          >
            <option value="">Все причины</option>
            {reasonOptions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="p-5"><SkeletonLines n={5} /></div>
      ) : error ? (
        <div className="p-6 text-center text-sm text-slate-500">
          Не удалось загрузить списания.{' '}
          <button type="button" className="text-brand-700 font-medium hover:underline" onClick={onRetry}>Повторить</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <div className="font-medium">За период ничего не списывали</div>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Кассиры списывают товар в кассе: «Поставки» → «Списать». Вручную — на вкладке «Позиции»:
            кнопка движения у позиции, операция «Списание».
          </p>
        </div>
      ) : groups.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-500">
          Списаний с причиной «{reason}» за период нет.{' '}
          <button type="button" className="text-brand-700 font-medium hover:underline" onClick={() => onReason('')}>
            Показать все
          </button>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {groups.map((g, i) => {
            const isOpen = open[g.key] ?? i === 0;
            const name = g.name ?? (g.manual ? 'Без имени' : 'Кассир без имени');
            const count = groupCount(g);
            return (
              <li key={g.key}>
                {/* Та же сетка, что у строк: сумма человека стоит над колонкой рублей его списаний. */}
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpen((o) => ({ ...o, [g.key]: !isOpen }))}
                  className={`w-full flex items-center gap-3 ${ROW_GRID} px-4 sm:px-5 py-3.5 text-left transition-colors hover:bg-slate-50
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400`}
                >
                  <span className="flex items-center gap-2 shrink-0">
                    <ChevronRight
                      size={16}
                      aria-hidden
                      className={`text-slate-500 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
                    />
                    <span
                      className={`w-9 h-9 rounded-full grid place-items-center text-xs font-semibold shrink-0 ${
                        g.manual ? 'bg-slate-100 text-slate-600' : 'bg-brand-50 text-brand-700'
                      }`}
                      aria-hidden
                    >
                      {initials(g.name) || <UserRound size={16} />}
                    </span>
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium truncate">{name}</span>
                    <span className="block text-xs text-slate-500 truncate">{groupMeta(g)}</span>
                    <span className="block sm:hidden text-xs text-slate-500">{count.join(' · ')}</span>
                  </span>
                  <span className="hidden md:block h-1.5 rounded-full bg-slate-100 overflow-hidden" aria-hidden>
                    <span
                      className="block h-full rounded-full bg-rose-400"
                      style={{ width: `${max > 0 && g.total > 0 ? Math.max(4, (g.total / max) * 100) : 0}%` }}
                    />
                  </span>
                  <span
                    className={`sm:col-start-4 text-right font-semibold tabular-nums whitespace-nowrap shrink-0 ${
                      g.total === 0 ? 'text-slate-500' : ''
                    }`}
                  >
                    {formatRub(g.total)}
                  </span>
                  <span className="hidden sm:block text-right text-xs leading-snug text-slate-500">
                    {count.map((c) => <span key={c} className="block">{c}</span>)}
                  </span>
                </button>
                {isOpen && (
                  <ul className="border-t border-slate-100 bg-slate-50/50 divide-y divide-slate-100">
                    {g.rows.map((m) => (
                      <WriteOffRow key={m.id} m={m} showShift={g.shifts.length > 1} onCancel={onCancel} />
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {rows.length >= WRITE_OFF_LIMIT && (
        <p className="px-5 py-3 border-t border-slate-100 text-xs text-amber-700 bg-amber-50/60">
          Показаны последние {WRITE_OFF_LIMIT} списаний — суммы по людям неполные. Сузьте период, чтобы увидеть всё.
        </p>
      )}
    </section>
  );
}

function WriteOffRow({ m, showShift, onCancel }: {
  m: StockMovement;
  showShift: boolean;
  onCancel: (m: StockMovement) => void;
}) {
  const cancelled = Boolean(m.cancelledAt);
  const strike = cancelled ? 'line-through decoration-slate-300 text-slate-400' : '';
  const { text } = reasonAndNote(m);
  const qty = signedQty(m.quantity, m.stockItem.unit);
  // На телефоне две колонки: слева позиция с причиной, справа сумма и под ней кнопка.
  return (
    <li className={`px-4 sm:px-5 py-3 sm:py-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 ${ROW_GRID} gap-y-2 items-center`}>
      <div className="hidden sm:block text-sm tabular-nums leading-tight">
        <div className="text-slate-600">{shortDate(m.createdAt)}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          {shortTime(m.createdAt)}
          {showShift && m.shiftNumber != null && <> · №{m.shiftNumber}</>}
        </div>
      </div>
      <div className="min-w-0 row-span-2 sm:row-span-1 self-start sm:self-center">
        <div className={`font-medium truncate ${strike}`}>{m.stockItem.name}</div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs text-slate-500">
          <Badge tone={cancelled ? 'slate' : 'danger'}>{reasonOf(m)}</Badge>
          {text && <span className="break-words min-w-0">{text}</span>}
        </div>
        <div className="sm:hidden text-xs text-slate-500 tabular-nums mt-1">{shortDateTime(m.createdAt)}</div>
        <CancelNote m={m} />
        <BlockedNote m={m} />
      </div>
      <div className={`hidden sm:block text-right tabular-nums font-medium whitespace-nowrap ${cancelled ? strike : 'text-rose-600'}`}>
        {qty}
      </div>
      <div className="text-right tabular-nums whitespace-nowrap self-start sm:self-center">
        <span className={strike}>{formatRub(movementCost(m))}</span>
        <div className={`sm:hidden text-xs font-medium ${cancelled ? strike : 'text-rose-600'}`}>{qty}</div>
      </div>
      <div className={`col-start-2 sm:col-start-auto justify-end self-end sm:self-center ${cancelled ? 'hidden sm:flex' : 'flex'}`}>
        <WriteOffAction m={m} onCancel={onCancel} />
      </div>
    </li>
  );
}

function CancelNote({ m }: { m: StockMovement }) {
  if (!m.cancelledAt) return null;
  return (
    <div className="text-xs text-slate-500 mt-1">
      Отменено{m.cancelledByName ? ` · ${m.cancelledByName}` : ''} · {shortDateTime(m.cancelledAt)}
      {m.cancelComment ? ` — ${m.cancelComment}` : ''}
    </div>
  );
}

/** Почему отмена недоступна — видимой строкой: подсказку при наведении не увидят с телефона. */
function BlockedNote({ m }: { m: StockMovement }) {
  const b = m.cancelBlockedBy;
  if (m.cancelledAt || !b) return null;
  return (
    <div className="text-xs text-slate-500 mt-1">
      {b.kind === 'RESET'
        ? `После списания остатки склада сбросили до нуля (${shortDate(b.at)}) — вернуть товар поверх нуля нельзя.`
        : `После списания была инвентаризация №${b.number} — остаток уже пересчитан по факту.`}
    </div>
  );
}

/** Кнопка отмены у списания: доступна, отменено или заперто инвентаризацией. */
function WriteOffAction({ m, onCancel }: { m: StockMovement; onCancel: (m: StockMovement) => void }) {
  if (m.type !== 'WRITE_OFF') return null;
  if (m.cancelledAt) return <Badge tone="slate">Отменено</Badge>;
  if (m.cancelBlockedBy != null) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 whitespace-nowrap">
        <Lock size={13} aria-hidden /> Нельзя отменить
      </span>
    );
  }
  return (
    <button type="button" className="btn-outline px-2.5 py-1 text-xs whitespace-nowrap" onClick={() => onCancel(m)}>
      <Undo2 size={14} aria-hidden /> Отменить
    </button>
  );
}

// ─── Причины и частые позиции ────────────────────────────────────────────

function ReasonsCard({ data, loading, reason, onReason }: {
  data: StockAnalytics | undefined;
  loading: boolean;
  reason: string;
  onReason: (r: string) => void;
}) {
  if (loading) return <div className="card p-5"><SkeletonLines n={7} /></div>;
  if (!data) return null;
  const reasons = data.writeOffReasons;
  const max = reasons.reduce((m, r) => Math.max(m, r.cost), 0);
  const top = [...data.byItem]
    .filter((i) => i.writeOffCost > 0)
    .sort((a, b) => b.writeOffCost - a.writeOffCost)
    .slice(0, 5);
  return (
    <aside className="card p-5 grid gap-6 content-start" aria-label="Причины списаний">
      <section>
        <h3 className="text-sm font-semibold mb-2">Причины</h3>
        {reasons.length === 0 ? (
          <p className="text-sm text-slate-500">За период ничего не списывали.</p>
        ) : (
          <>
            <ul className="grid gap-0.5 -mx-2">
              {reasons.map((r) => {
                const on = reason === r.label;
                return (
                  <li key={r.label}>
                    <button
                      type="button"
                      aria-pressed={on}
                      onClick={() => onReason(on ? '' : r.label)}
                      className={`w-full rounded-lg px-2 py-1.5 text-left grid gap-1.5 transition-colors
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
                          on ? 'bg-brand-50 ring-1 ring-brand-200' : 'hover:bg-slate-50'
                        }`}
                    >
                      <span className="flex justify-between gap-3 text-sm">
                        <span className={on ? 'text-brand-700 font-medium' : 'text-slate-600'}>{r.label}</span>
                        <span className="tabular-nums font-medium">{formatRub(r.cost)}</span>
                      </span>
                      <span className="h-1.5 rounded-full bg-slate-100 overflow-hidden" aria-hidden>
                        <span
                          className="block h-full rounded-full bg-rose-400"
                          style={{ width: `${max > 0 ? Math.max(3, (r.cost / max) * 100) : 0}%` }}
                        />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {reasons.length > 1 && (
              <p className="text-xs text-slate-500 mt-2">Нажмите на причину — в «Кто списывал» останутся только её списания.</p>
            )}
          </>
        )}
      </section>
      {top.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold mb-2">Что списывают чаще</h3>
          <ul className="grid gap-2 text-sm">
            {top.map((i) => (
              <li key={i.id} className="flex justify-between gap-3">
                <span className="truncate text-slate-700">{i.name}</span>
                <span className="tabular-nums shrink-0 text-slate-500">
                  {formatQuantity(i.writeOff, i.unit)} · <span className="text-slate-900 font-medium">{formatRub(i.writeOffCost)}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </aside>
  );
}

// ─── Движение склада ─────────────────────────────────────────────────────

function MovementSummary({ data }: { data: StockAnalytics }) {
  const s = data.summary;
  const salesNet = s.sales - s.returns;
  const inventory = s.inventorySurplus - s.inventoryShortage;
  const cells: Array<{ label: string; value: string; hint: string; tone?: string }> = [
    {
      label: 'Приход',
      value: formatRub(s.arrival),
      hint: 'Поставки и ручной приход',
      tone: s.arrival > 0 ? 'text-emerald-700' : undefined,
    },
    {
      label: 'Продажи',
      value: formatRub(salesNet),
      hint: s.returns > 0
        ? `Себестоимость по тех-картам, возвраты при отмене −${formatRub(s.returns)}`
        : 'Себестоимость по тех-картам',
    },
    { label: 'Ручной расход', value: formatRub(s.consumption), hint: 'Расход не на продажу и не в списание' },
    {
      label: 'Инвентаризация',
      value: signedRub(inventory),
      hint: `Излишки ${formatRub(s.inventorySurplus)} · недостача ${formatRub(s.inventoryShortage)}`,
      tone: inventory < 0 ? 'text-rose-700' : inventory > 0 ? 'text-emerald-700' : undefined,
    },
  ];
  const resetIn = s.resetIn ?? 0;
  const resetOut = s.resetOut ?? 0;
  if (resetIn > 0 || resetOut > 0) {
    cells.push({
      label: 'Сброс остатка',
      value: signedRub(resetIn - resetOut),
      hint: 'Обнуление на вкладке «Позиции» — в потери не входит',
    });
  }
  return (
    <section className="card overflow-hidden" aria-labelledby="stock-flow">
      <div className="px-4 sm:px-5 py-3.5 border-b border-slate-100 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="stock-flow" className="font-semibold">Движение склада</h2>
        <span className="text-sm text-slate-500">
          по себестоимости · {s.movements} {plural(s.movements, ['операция', 'операции', 'операций'])} за период
        </span>
      </div>
      <dl className={`grid grid-cols-2 ${cells.length > 4 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-px bg-slate-100`}>
        {cells.map((c, i) => (
          <div
            key={c.label}
            className={`bg-white px-4 sm:px-5 py-4 min-w-0 ${
              i === cells.length - 1 && cells.length % 2 === 1 ? 'col-span-2 lg:col-span-1' : ''
            }`}
          >
            <dt className="text-sm text-slate-500">{c.label}</dt>
            <dd className={`text-xl font-semibold tabular-nums mt-0.5 ${c.tone ?? 'text-slate-900'}`}>{c.value}</dd>
            <dd className="text-xs text-slate-500 mt-1">{c.hint}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

// ─── Операции и позиции ──────────────────────────────────────────────────

type DetailsTab = 'journal' | 'turnover';

function DetailsCard({ from, to, data, onCancel }: {
  from: string;
  to: string;
  data: StockAnalytics | undefined;
  onCancel: (m: StockMovement) => void;
}) {
  const [tab, setTab] = useState<DetailsTab>('journal');
  return (
    <section className="card overflow-hidden" aria-label="Подробности">
      <div role="tablist" aria-label="Подробности" className="px-4 sm:px-5 flex gap-6 border-b border-slate-100">
        <DetailsTabButton id="journal" active={tab === 'journal'} onClick={() => setTab('journal')}>Операции</DetailsTabButton>
        <DetailsTabButton id="turnover" active={tab === 'turnover'} onClick={() => setTab('turnover')}>По позициям</DetailsTabButton>
      </div>
      <div role="tabpanel" id={`stock-panel-${tab}`} aria-labelledby={`stock-tab-${tab}`}>
        {tab === 'journal' ? (
          <MovementJournal from={from} to={to} onCancel={onCancel} />
        ) : data ? (
          <Turnover data={data} />
        ) : (
          <Spinner />
        )}
      </div>
    </section>
  );
}

function DetailsTabButton({ id, active, onClick, children }: {
  id: DetailsTab;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={`stock-tab-${id}`}
      aria-selected={active}
      aria-controls={`stock-panel-${id}`}
      onClick={onClick}
      className={`py-3.5 -mb-px border-b-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:text-brand-700 ${
        active ? 'border-brand-600 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'
      }`}
    >
      {children}
    </button>
  );
}

const movementLabels: Record<StockMovementType, string> = {
  ARRIVAL: 'Приход',
  CONSUMPTION: 'Расход',
  WRITE_OFF: 'Списание',
  INVENTORY: 'Инвентаризация',
  SALE: 'Продажа',
};
const movementTone: Record<StockMovementType, 'success' | 'warning' | 'danger' | 'info' | 'slate'> = {
  ARRIVAL: 'success',
  CONSUMPTION: 'warning',
  WRITE_OFF: 'danger',
  INVENTORY: 'info',
  SALE: 'slate',
};

/** «Сброс остатка» — это INVENTORY с причиной RESET, а не отдельный тип. */
const operationLabel = (m: StockMovement) =>
  m.reason === STOCK_RESET_REASON ? 'Сброс остатка' : movementLabels[m.type];

type JournalFilter = 'all' | 'writeoff' | 'sale' | 'return' | 'supply' | 'manual' | 'inventory';
const JOURNAL_FILTERS: Array<{ id: JournalFilter; label: string; type?: StockMovementType; source?: StockMovementSource }> = [
  { id: 'all', label: 'Все' },
  { id: 'writeoff', label: 'Списания', type: 'WRITE_OFF' },
  { id: 'sale', label: 'Продажи', source: 'SALE' },
  { id: 'return', label: 'Возвраты при отмене', source: 'RETURN' },
  { id: 'supply', label: 'Поставки', source: 'SUPPLY' },
  { id: 'manual', label: 'Вручную', source: 'ADMIN' },
  { id: 'inventory', label: 'Инвентаризация', source: 'INVENTORY' },
];

const PAGE = 100;

function MovementJournal({ from, to, onCancel }: { from: string; to: string; onCancel: (m: StockMovement) => void }) {
  const [filter, setFilter] = useState<JournalFilter>('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const current = JOURNAL_FILTERS.find((f) => f.id === filter) ?? JOURNAL_FILTERS[0];

  // Поиск идёт на сервер: без паузы каждая буква — отдельный запрос.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin/stock/movements', 'journal', from, to, filter, query, limit],
    queryFn: () =>
      listStockMovements({
        dateFrom: from,
        dateTo: to,
        type: current.type,
        source: current.source,
        q: query || undefined,
        limit,
        withCancelled: true,
      }),
    placeholderData: (prev) => prev,
  });
  const rows = data ?? [];
  const canLoadMore = rows.length >= limit && limit < 1000;

  const exportCsv = () => {
    downloadCsv(`stock-movements-${from}_${to}.csv`, [
      ['Дата', 'Позиция', 'Операция', 'Источник', 'Количество', 'Ед.', 'Цена/ед., ₽', 'Сумма, ₽', 'Причина', 'Комментарий', 'Отменено'],
      ...rows.map((m) => [
        new Date(m.createdAt).toLocaleString('ru-RU'),
        m.stockItem.name,
        operationLabel(m),
        m.sourceLabel ?? '',
        String(m.quantity),
        m.stockItem.unit,
        (m.costPerUnit / 100).toFixed(2),
        ((m.quantity * m.costPerUnit) / 100).toFixed(2),
        m.reasonLabel ?? '',
        m.note ?? m.comment ?? '',
        m.cancelledAt
          ? [new Date(m.cancelledAt).toLocaleString('ru-RU'), m.cancelledByName, m.cancelComment].filter(Boolean).join(' · ')
          : '',
      ]),
    ]);
  };

  return (
    <>
      <div className="px-4 sm:px-5 py-4 grid gap-3 border-b border-slate-100">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-500">Каждая операция со складом за период и кто её провёл</p>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
              <input
                className="input pl-9 sm:w-56"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setLimit(PAGE); }}
                placeholder="Найти позицию"
                aria-label="Найти позицию в журнале"
              />
            </div>
            <button type="button" className="btn-outline" onClick={exportCsv} disabled={rows.length === 0}>
              <FileDown size={16} aria-hidden /> CSV
            </button>
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap" role="group" aria-label="Тип операций">
          {JOURNAL_FILTERS.map((f) => (
            <FilterChip key={f.id} active={filter === f.id} onClick={() => { setFilter(f.id); setLimit(PAGE); }}>
              {f.label}
            </FilterChip>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[980px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left font-medium px-4 py-3">Дата</th>
                <th className="text-left font-medium px-4 py-3">Позиция</th>
                <th className="text-left font-medium px-4 py-3">Операция</th>
                <th className="text-right font-medium px-4 py-3">Кол-во</th>
                <th className="text-right font-medium px-4 py-3">Сумма</th>
                <th className="text-left font-medium px-4 py-3">Комментарий</th>
                <th className="px-4 py-3"><span className="sr-only">Действие</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const { reason, text } = reasonAndNote(m);
                const waste = m.wasteQuantity != null && m.wasteQuantity > 0 ? m.wasteQuantity : 0;
                const cancelled = Boolean(m.cancelledAt);
                const isReset = m.reason === STOCK_RESET_REASON;
                const strike = cancelled ? 'line-through decoration-slate-300 text-slate-400' : '';
                return (
                  <tr key={m.id} className="table-row align-top">
                    <td className="px-4 py-2.5 whitespace-nowrap text-slate-600 tabular-nums">{shortDateTime(m.createdAt)}</td>
                    <td className={`px-4 py-2.5 font-medium ${strike}`}>{m.stockItem.name}</td>
                    <td className="px-4 py-2.5">
                      <Badge tone={cancelled || isReset ? 'slate' : movementTone[m.type]}>{operationLabel(m)}</Badge>
                      {isReset
                        ? m.actorName && <div className="text-xs text-slate-500 mt-1">{m.actorName}</div>
                        : m.sourceLabel && <div className="text-xs text-slate-500 mt-1">{m.sourceLabel}</div>}
                    </td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-medium whitespace-nowrap ${
                      cancelled ? strike : m.quantity < 0 ? 'text-rose-600' : 'text-emerald-700'
                    }`}>
                      {signedQty(m.quantity, m.stockItem.unit)}
                    </td>
                    <td className={`px-4 py-2.5 text-right tabular-nums whitespace-nowrap ${strike}`}>
                      {formatRub(movementCost(m))}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 max-w-[320px]">
                      {reason ? <span className="text-slate-700">{reason}</span> : null}
                      {reason && text ? ' — ' : null}
                      {text}
                      {waste > 0 ? (
                        <div className="text-xs text-amber-600">в т.ч. отход {formatQuantity(waste, m.stockItem.unit)}</div>
                      ) : null}
                      {!reason && !text && waste === 0 && !cancelled && m.cancelBlockedBy == null ? '—' : null}
                      <CancelNote m={m} />
                      <BlockedNote m={m} />
                    </td>
                    <td className="px-4 py-2 text-right w-36">
                      <WriteOffAction m={m} onCancel={onCancel} />
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-slate-500 py-10">
                    {query ? `По запросу «${query}» операций нет` : 'За период операций нет'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {canLoadMore && (
        <div className="p-3 border-t border-slate-100 text-center">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => setLimit((l) => Math.min(1000, l + PAGE))}
            disabled={isFetching}
          >
            {isFetching ? 'Загружаем…' : 'Показать ещё'}
          </button>
        </div>
      )}
    </>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
        active ? 'bg-brand-600 text-white border-brand-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
      }`}
    >
      {children}
    </button>
  );
}

function Turnover({ data }: { data: StockAnalytics }) {
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.byItem.filter((i) => i.name.toLowerCase().includes(q)) : data.byItem;
  }, [data.byItem, search]);
  const totalOut = rows.reduce((s, i) => s + i.outCost, 0);

  const exportCsv = () => {
    downloadCsv(`stock-turnover-${data.from}_${data.to}.csv`, [
      ['Позиция', 'Ед.', 'На начало', 'Приход', 'Возвраты', 'Продажи', 'Списания', 'Расход', 'Инвентаризация', 'В т.ч. сброс', 'На конец', 'Ушло, ₽', 'Списано, ₽', 'Отход, ₽'],
      ...rows.map((i) => [
        i.name, i.unit,
        trimNumber(i.opening), trimNumber(i.arrival), trimNumber(i.returns), trimNumber(i.sales),
        trimNumber(i.writeOff), trimNumber(i.consumption), trimNumber(i.inventory), trimNumber(i.reset ?? 0),
        trimNumber(i.closing),
        (i.outCost / 100).toFixed(2), (i.writeOffCost / 100).toFixed(2), (i.wasteCost / 100).toFixed(2),
      ]),
    ]);
  };

  return (
    <>
      <div className="px-4 sm:px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100">
        <p className="text-sm text-slate-500">
          Сколько было, пришло и ушло по каждой позиции. Только позиции с движениями за период.
        </p>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
            <input
              className="input pl-9 sm:w-56"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Найти позицию"
              aria-label="Найти позицию в ведомости"
            />
          </div>
          <button type="button" className="btn-outline" onClick={exportCsv} disabled={rows.length === 0}>
            <FileDown size={16} aria-hidden /> CSV
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
              <th className="text-left font-medium px-4 py-3">Позиция</th>
              <th className="text-right font-medium px-3 py-3">Было</th>
              <th className="text-right font-medium px-3 py-3">Пришло</th>
              <th className="text-right font-medium px-3 py-3">Продано</th>
              <th className="text-right font-medium px-3 py-3">Списано</th>
              <th className="text-right font-medium px-3 py-3">Расход</th>
              <th className="text-right font-medium px-3 py-3">Инвентаризация</th>
              <th className="text-right font-medium px-3 py-3">Стало</th>
              <th
                className="text-right font-medium px-4 py-3"
                title="Себестоимость всего, что ушло со склада: продажи, списания, расход, недостача"
              >
                Ушло, ₽
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.id} className="table-row">
                <td className="px-4 py-2.5">
                  <div className="font-medium">{i.name}</div>
                  {i.wasteCost > 0 && <div className="text-xs text-amber-600">в т.ч. отход {formatRub(i.wasteCost)}</div>}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{qtyText(i.opening, i.unit)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">
                  {i.arrival + i.returns > 0 ? formatQuantity(i.arrival + i.returns, i.unit) : '—'}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{i.sales > 0 ? formatQuantity(i.sales, i.unit) : '—'}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-rose-600">{i.writeOff > 0 ? formatQuantity(i.writeOff, i.unit) : '—'}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{i.consumption > 0 ? formatQuantity(i.consumption, i.unit) : '—'}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums ${i.inventory < 0 ? 'text-rose-600' : i.inventory > 0 ? 'text-emerald-700' : ''}`}>
                  {i.inventory !== 0 ? signedQty(i.inventory, i.unit) : '—'}
                  {i.reset ? <div className="text-xs text-slate-500">в т.ч. сброс {signedQty(i.reset, i.unit)}</div> : null}
                </td>
                <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${i.closing < 0 ? 'text-rose-600' : ''}`}>
                  {qtyText(i.closing, i.unit)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatRub(i.outCost)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-slate-500 py-10">
                  {search.trim() ? `По запросу «${search.trim()}» позиций нет` : 'За период движений нет'}
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 1 && (
            <tfoot>
              <tr className="bg-slate-50/70">
                <td colSpan={8} className="px-4 py-3 text-right text-slate-500">Итого ушло со склада</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold">{formatRub(totalOut)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}

// ─── Отмена списания ─────────────────────────────────────────────────────

function CancelWriteOffModal({ movement: m, onClose }: { movement: StockMovement; onClose: () => void }) {
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin/stock/analytics'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/movements'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/items'] });
  };
  const mut = useMutation({
    mutationFn: () => cancelStockWriteOff(m.id, comment.trim() || undefined),
    onSuccess: (r) => {
      toast.success(
        `«${r.stockItem.name}»: вернули ${formatQuantity(r.returned, r.stockItem.unit)}, остаток ${qtyText(r.stockItem.quantity, r.stockItem.unit)}`,
      );
      refresh();
      onClose();
    },
    // Текст ошибки показывает общий перехватчик API. Уже отменено или заперто
    // инвентаризацией — данные устарели: обновляем и закрываем.
    onError: (err) => {
      const status = isAxiosError(err) ? err.response?.status : undefined;
      if (status === 404 || status === 409) {
        refresh();
        onClose();
      }
    },
  });
  const unit = m.stockItem.unit;
  const text = noteOf(m);

  return (
    <Modal
      open
      size="sm"
      onClose={() => { if (!mut.isPending) onClose(); }}
      title="Отменить списание?"
      footer={
        <>
          <button type="button" className="btn-outline" onClick={onClose} disabled={mut.isPending}>
            Не отменять
          </button>
          <button type="button" className="btn-primary" onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? 'Отменяем…' : 'Отменить списание'}
          </button>
        </>
      }
    >
      <div className="grid gap-4">
        <div className="rounded-lg border border-slate-200 px-3.5 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-medium">{m.stockItem.name}</span>
            <span className="tabular-nums font-medium text-rose-600 whitespace-nowrap">{signedQty(m.quantity, unit)}</span>
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {reasonOf(m)} · {formatRub(movementCost(m))}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {shortDateTime(m.createdAt)}{m.sourceLabel ? ` · ${m.sourceLabel}` : ''}
          </div>
          {text && <div className="text-xs text-slate-500 mt-1">«{text}»</div>}
        </div>
        <p className="text-sm text-slate-600">
          Остаток позиции увеличится на {formatQuantity(Math.abs(m.quantity), unit)}. Списание уйдёт из сумм
          и аналитики, а в журнале останется зачёркнутым — с пометкой, кто отменил.
        </p>
        <div>
          <label className="label" htmlFor="cancel-writeoff-comment">Почему отменяете — необязательно</label>
          <input
            id="cancel-writeoff-comment"
            className="input"
            maxLength={300}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !mut.isPending) mut.mutate(); }}
            placeholder="Например: списали не ту позицию"
            autoFocus
          />
        </div>
      </div>
    </Modal>
  );
}
